// src/pages/ConsultationBookingPage.jsx
import React, { useState, useEffect } from 'react';
import { useLocalizedNavigate } from '../hooks/useLocalizedNavigate';
import { Calendar, Clock, Lock, CheckCircle, AlertCircle, ArrowLeft, Mail, Video, Sparkles, Edit, Trash2, X, ArrowUpRight } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { auth } from '../firebase';
import consultationService from '../services/consultationService';
import subscriptionService from '../services/subscriptionService';
import adminService from '../services/adminService';
import LogamiLogoSpinner from '../components/ui/LogamiLogoSpinner';

const ELEVATED_SURFACE =
  'relative overflow-hidden rounded-2xl border-2 border-black/10 bg-white shadow-[0_10px_28px_rgba(15,23,42,0.06)]';

const DEFAULT_EXAM_CONTEXT = { provider: 'TELC', level: 'B1' };

const parseExamContext = (rawExamData) => {
  if (!rawExamData) return null;

  try {
    const parsed = JSON.parse(rawExamData);
    const provider = parsed?.examProvider || parsed?.provider;
    const level = parsed?.level || parsed?.examLevel;

    if (!provider || !level) return null;

    return {
      provider: String(provider).toUpperCase(),
      level: String(level).toUpperCase(),
    };
  } catch {
    return null;
  }
};

const resolveSelectedExamContext = (userId) => {
  const userScopedKey = userId ? `selectedExamData_${userId}` : null;
  const candidates = [
    userScopedKey ? localStorage.getItem(userScopedKey) : null,
    sessionStorage.getItem('selectedExamData'),
    localStorage.getItem('selectedExamData'),
  ];

  for (const candidate of candidates) {
    const parsed = parseExamContext(candidate);
    if (parsed) return parsed;
  }

  return DEFAULT_EXAM_CONTEXT;
};

const ConsultationBookingPage = () => {
  const { t, i18n } = useTranslation();
  const navigate = useLocalizedNavigate();
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedTimeSlot, setSelectedTimeSlot] = useState(null);
  const [availableSlots, setAvailableSlots] = useState([]);
  const [loading, setLoading] = useState(false);
  const [booking, setBooking] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [subscriptionStatus, setSubscriptionStatus] = useState(null);
  const [remainingConsultations, setRemainingConsultations] = useState(0);
  const [userAppointments, setUserAppointments] = useState([]);
  const [notes, setNotes] = useState('');
  const [editingAppointment, setEditingAppointment] = useState(null);
  const [editDate, setEditDate] = useState('');
  const [editTimeSlot, setEditTimeSlot] = useState('');
  const [editNotes, setEditNotes] = useState('');
  const [editAvailableSlots, setEditAvailableSlots] = useState([]);
  const [deletingAppointment, setDeletingAppointment] = useState(null);
  const [isConsultationLocked, setIsConsultationLocked] = useState(false);
  const examContext = resolveSelectedExamContext(auth.currentUser?.uid);

  useEffect(() => {
    const loadData = async () => {
      if (!auth.currentUser) {
        navigate('/auth');
        return;
      }

      setLoading(true);
      setIsConsultationLocked(false);
      try {
        // Check subscription status
        const status = await subscriptionService.getSubscriptionStatus(auth.currentUser.uid);
        setSubscriptionStatus(status);

        // Premium, pre-Oct-2026 monthly/quarterly customers and admins can book
        const isAdminUser = adminService.isAdmin();
        const hasAccess = isAdminUser || await consultationService.hasConsultationAccess(auth.currentUser.uid);
        if (!hasAccess) {
          setError(t('consultation.premiumOnly'));
          setIsConsultationLocked(true);
          setLoading(false);
          return;
        }

        // Get remaining consultations (Premium: 5; older plans keep their 1 / 3)
        const remaining = await consultationService.getRemainingConsultations(auth.currentUser.uid);
        setRemainingConsultations(remaining);

        // Get user's existing appointments
        const appointments = await consultationService.getUserAppointments(auth.currentUser.uid);
        setUserAppointments(appointments);
      } catch (err) {
        console.error('Error loading consultation data:', err);
        setError(t('consultation.loadError'));
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [navigate, t]);

  useEffect(() => {
    const loadAvailableSlots = async () => {
      if (!selectedDate) {
        setAvailableSlots([]);
        return;
      }

      // Check if selected date is Monday, Tuesday, or Thursday
      const date = new Date(selectedDate + 'T00:00:00');
      const dayOfWeek = date.getDay();
      if (dayOfWeek !== 1 && dayOfWeek !== 2 && dayOfWeek !== 4) {
        setAvailableSlots([]);
        return;
      }

      setLoading(true);
      try {
        const slots = await consultationService.getAvailableTimeSlots(selectedDate);
        setAvailableSlots(slots);
      } catch (err) {
        console.error('Error loading available slots:', err);
        setError(t('consultation.slotsError'));
      } finally {
        setLoading(false);
      }
    };

    loadAvailableSlots();
  }, [selectedDate, t]);

  const handleDateSelect = (date) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const selected = new Date(date + 'T00:00:00');
    selected.setHours(0, 0, 0, 0);

    if (selected < today) {
      setError(t('consultation.pastDate'));
      return;
    }

    // Check if selected date is Monday, Tuesday, or Thursday
    const dayOfWeek = selected.getDay(); // 0 = Sunday, 1 = Monday, 2 = Tuesday, 3 = Wednesday, 4 = Thursday, 5 = Friday, 6 = Saturday
    if (dayOfWeek !== 1 && dayOfWeek !== 2 && dayOfWeek !== 4) {
      setError(t('consultation.unavailableDay'));
      return;
    }

    setSelectedDate(date);
    setSelectedTimeSlot(null);
    setError(null);
  };

  const handleBookAppointment = async () => {
    if (!selectedDate || !selectedTimeSlot) {
      setError(t('consultation.selectDateAndTime'));
      return;
    }

    // Validate that the selected date is Monday, Tuesday, or Thursday
    const date = new Date(selectedDate + 'T00:00:00');
    const dayOfWeek = date.getDay();
    if (dayOfWeek !== 1 && dayOfWeek !== 2 && dayOfWeek !== 4) {
      setError(t('consultation.unavailableDay'));
      return;
    }

    // TEMPORARY: Comment out remaining consultations check
    // if (remainingConsultations <= 0) {
    //   setError(t('consultation.noRemaining'));
    //   return;
    // }

    setBooking(true);
    setError(null);

    try {
      const result = await consultationService.bookAppointment({
        date: selectedDate,
        timeSlot: selectedTimeSlot,
        notes: notes.trim()
      });

      if (result.success) {
        setSuccess(true);
        setSelectedDate(null);
        setSelectedTimeSlot(null);
        setNotes('');
        
        // Refresh remaining consultations
        const remaining = await consultationService.getRemainingConsultations(auth.currentUser.uid);
        setRemainingConsultations(remaining);

        // Refresh appointments
        const appointments = await consultationService.getUserAppointments(auth.currentUser.uid);
        setUserAppointments(appointments);

        // Refresh available slots to reflect the booked slot is now unavailable
        if (selectedDate) {
          const slots = await consultationService.getAvailableTimeSlots(selectedDate);
          setAvailableSlots(slots);
          // Clear selected time slot since it's now booked
          setSelectedTimeSlot(null);
        }

        setTimeout(() => {
          setSuccess(false);
        }, 5000);
      } else {
        setError(result.error || t('consultation.bookingError'));
      }
    } catch (err) {
      console.error('Error booking appointment:', err);
      setError(t('consultation.bookingError'));
    } finally {
      setBooking(false);
    }
  };

  const getMinDate = () => {
    const today = new Date();
    return today.toISOString().split('T')[0];
  };

  const getMaxDate = () => {
    const maxDate = new Date();
    maxDate.setMonth(maxDate.getMonth() + 3); // 3 months ahead
    return maxDate.toISOString().split('T')[0];
  };

  // Disable weekends and Wed/Fri in date picker
  const isDateDisabled = (dateString) => {
    if (!dateString) return false;
    const date = new Date(dateString + 'T00:00:00');
    const dayOfWeek = date.getDay(); // 0 = Sunday, 1 = Monday, 2 = Tuesday, 3 = Wednesday, 4 = Thursday, 5 = Friday, 6 = Saturday
    
    // Disable weekends (0 = Sunday, 6 = Saturday) and Wednesday (3), Friday (5)
    // Only enable Monday (1), Tuesday (2), Thursday (4)
    return dayOfWeek === 0 || dayOfWeek === 3 || dayOfWeek === 5 || dayOfWeek === 6;
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString(t('locale') || 'en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  // Check if more than 24 hours remain until appointment
  const canEditOrDelete = (appointment) => {
    if (!appointment.date || !appointment.timeSlot) return false;
    const appointmentDateTime = new Date(`${appointment.date}T${appointment.timeSlot}:00`);
    const now = new Date();
    const hoursUntilAppointment = (appointmentDateTime - now) / (1000 * 60 * 60);
    return hoursUntilAppointment > 24;
  };

  // Handle edit appointment
  const handleEditAppointment = async (appointment) => {
    if (!canEditOrDelete(appointment)) {
      setError(t('consultation.cannotEditLessThan24Hours'));
      return;
    }

    setEditingAppointment(appointment);
    setEditDate(appointment.date);
    setEditTimeSlot(appointment.timeSlot);
    setEditNotes(appointment.notes || '');
    setError(null);

    // Load available slots for the appointment date
    try {
      const slots = await consultationService.getAvailableTimeSlots(appointment.date);
      // Include the current time slot even if it's "booked" (since it's this appointment)
      if (!slots.includes(appointment.timeSlot)) {
        slots.push(appointment.timeSlot);
        slots.sort();
      }
      setEditAvailableSlots(slots);
    } catch (err) {
      console.error('Error loading available slots:', err);
    }
  };

  // Handle save edited appointment
  const handleSaveEdit = async () => {
    if (!editDate || !editTimeSlot) {
      setError(t('consultation.selectDateAndTime'));
      return;
    }

    setBooking(true);
    setError(null);

    try {
      const result = await consultationService.updateAppointment(editingAppointment.id, {
        date: editDate,
        timeSlot: editTimeSlot,
        notes: editNotes.trim()
      });

      if (result.success) {
        setSuccess(true);
        setEditingAppointment(null);
        setEditDate('');
        setEditTimeSlot('');
        setEditNotes('');

        // Refresh appointments
        const appointments = await consultationService.getUserAppointments(auth.currentUser.uid);
        setUserAppointments(appointments);

        setTimeout(() => {
          setSuccess(false);
        }, 5000);
      } else {
        setError(result.error || t('consultation.updateError'));
      }
    } catch (err) {
      console.error('Error updating appointment:', err);
      setError(t('consultation.updateError'));
    } finally {
      setBooking(false);
    }
  };

  // Handle delete appointment
  const handleDeleteAppointment = async (appointment) => {
    if (!canEditOrDelete(appointment)) {
      setError(t('consultation.cannotDeleteLessThan24Hours'));
      return;
    }

    if (!window.confirm(t('consultation.confirmDelete'))) {
      return;
    }

    setDeletingAppointment(appointment.id);
    setError(null);

    try {
      const result = await consultationService.deleteAppointment(appointment.id);

      if (result.success) {
        setSuccess(true);

        // Refresh appointments
        const appointments = await consultationService.getUserAppointments(auth.currentUser.uid);
        setUserAppointments(appointments);

        // Refresh remaining consultations
        const remaining = await consultationService.getRemainingConsultations(auth.currentUser.uid);
        setRemainingConsultations(remaining);

        setTimeout(() => {
          setSuccess(false);
        }, 5000);
      } else {
        setError(result.error || t('consultation.deleteError'));
      }
    } catch (err) {
      console.error('Error deleting appointment:', err);
      setError(t('consultation.deleteError'));
    } finally {
      setDeletingAppointment(null);
    }
  };

  // Handle edit date change
  const handleEditDateChange = async (date) => {
    setEditDate(date);
    setEditTimeSlot('');

    if (date) {
      try {
        const slots = await consultationService.getAvailableTimeSlots(date);
        setEditAvailableSlots(slots);
      } catch (err) {
        console.error('Error loading available slots:', err);
        setEditAvailableSlots([]);
      }
    } else {
      setEditAvailableSlots([]);
    }
  };

  if (loading && !subscriptionStatus) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <LogamiLogoSpinner size="xl" className="mx-auto mb-4" decorative={false} alt="Logami loading" />
          <p className="text-black/60">{t('consultation.loading')}</p>
        </div>
      </div>
    );
  }

  if (isConsultationLocked) {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 py-8">
          <button
            onClick={() => navigate('/dashboard')}
            className="flex items-center gap-2 text-black/60 hover:text-black mb-6 transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
            <span>{t('common.back')}</span>
          </button>

          <div className={`${ELEVATED_SURFACE} max-w-xl mx-auto p-6 sm:p-8`}>
            <div className="text-center">
              <div className="flex justify-center mb-4">
                <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-gradient-to-br from-purple-100 to-indigo-100">
                  <Lock className="w-7 h-7 text-indigo-600" />
                </div>
              </div>
              <h1 className="text-xl sm:text-2xl font-black text-gray-900 mb-2">
                {t('consultation.premiumCoach.title')}
              </h1>
              <p className="text-sm text-gray-600 mb-3">
                {t('consultation.premiumCoach.body', {
                  provider: examContext.provider,
                  level: examContext.level,
                })}
              </p>
              <p className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-6">
                {t('consultation.premiumCoach.subtext')}
              </p>

              <div className="space-y-3">
                <button
                  onClick={() => navigate('/pricing')}
                  className="w-full min-h-[48px] inline-flex items-center justify-center px-4 py-3 rounded-xl bg-gradient-to-r from-black via-gray-900 to-[#1f3b63] text-white font-bold text-sm hover:shadow-[0_8px_20px_rgba(17,24,39,0.28)] active:scale-[0.98] touch-manipulation transition-all duration-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#3A86FF]/40"
                >
                  <ArrowUpRight className="w-4 h-4 mr-2" />
                  {t('consultation.premiumCoach.cta')}
                </button>

                <button
                  onClick={() => navigate('/dashboard')}
                  className="w-full min-h-[48px] inline-flex items-center justify-center px-4 py-3 rounded-xl border-2 border-black/10 bg-white text-gray-700 font-semibold text-sm hover:border-black/30 hover:bg-gray-50 touch-manipulation transition-all duration-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#3A86FF]/40"
                >
                  {t('consultation.premiumCoach.secondary')}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="relative min-h-screen bg-gray-50">
      {/* Geometric Background - Same as Dashboard */}
      {/* Main Content */}
      <div className="relative z-10 max-w-6xl mx-auto px-4 py-8">
        {/* Header */}
        <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-2 text-black/60 hover:text-black mb-6 transition-colors"
        >
          <ArrowLeft className="w-5 h-5" />
          <span>{t('common.back')}</span>
        </button>

        <div className="mb-8">
          <h1 className="text-4xl font-black text-black mb-2">{t('consultation.title')}</h1>
          <p className="text-lg text-black/60">{t('consultation.subtitle')}</p>
        </div>

        {/* Remaining Consultations - Enhanced */}
        <div 
          className="group relative bg-black text-white rounded-2xl p-6 mb-8 border-[3px] border-black overflow-hidden transition-all duration-500 hover:scale-[1.02] hover:shadow-2xl"
          style={{
            animation: 'slideInScale 0.5s ease-out',
            boxShadow: '0 20px 60px rgba(0, 0, 0, 0.2)'
          }}
        >
          {/* Decorative circles */}
          <div className="absolute top-0 right-0 w-32 h-32 bg-[#3A86FF] opacity-20 rounded-full transform -translate-y-16 translate-x-16 group-hover:scale-110 transition-transform duration-700" />
          
          {/* Corner decorations */}
          <div className="absolute top-3 left-3 w-5 h-5 border-t-2 border-l-2 border-white/20" />
          <div className="absolute bottom-3 right-3 w-5 h-5 border-b-2 border-r-2 border-white/20" />
          
          <div className="relative z-10 flex items-center justify-between">
            <div>
              <p className="text-white/60 text-sm uppercase tracking-wider mb-1">{t('consultation.remaining')}</p>
              <p className="text-5xl font-black group-hover:scale-110 transition-transform duration-300">{remainingConsultations}</p>
            </div>
            <div className="w-16 h-16 bg-[#3A86FF] rounded-xl flex items-center justify-center group-hover:scale-110 group-hover:rotate-12 transition-all duration-300 border-2 border-white/10">
              <Calendar className="w-8 h-8" />
            </div>
          </div>
          
          {/* Shine effect */}
          <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-1000" />
        </div>

        {/* Success Message with Animation */}
        {success && (
          <div 
            className="relative bg-gradient-to-r from-green-50 to-emerald-50 border-2 border-green-500 rounded-2xl p-6 mb-6 shadow-xl overflow-hidden"
            style={{
              animation: 'slideInScale 0.5s ease-out'
            }}
          >
            {/* Decorative background */}
            <div className="absolute top-0 right-0 w-24 h-24 bg-green-500 opacity-10 rounded-full transform -translate-y-12 translate-x-12" />
            
            <div className="relative z-10 flex items-start gap-4">
              <div className="w-12 h-12 bg-green-500 rounded-full flex items-center justify-center flex-shrink-0 animate-pulse shadow-lg">
                <CheckCircle className="w-7 h-7 text-white" />
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-black text-green-900 mb-3">{t('consultation.bookingSuccess')}</h3>
                <div className="space-y-2.5 text-sm text-green-800">
                  <div className="flex items-center gap-2 bg-white/50 rounded-lg px-3 py-2">
                    <Mail className="w-4 h-4 text-green-600 flex-shrink-0" />
                    <p className="font-medium">{t('consultation.emailVerification')}</p>
                  </div>
                  <div className="flex items-center gap-2 bg-white/50 rounded-lg px-3 py-2">
                    <Video className="w-4 h-4 text-green-600 flex-shrink-0" />
                    <p className="font-medium">{t('consultation.zoomMeeting')}</p>
                  </div>
                </div>
              </div>
              <div className="absolute top-3 right-3">
                <Sparkles className="w-5 h-5 text-green-500 animate-pulse" />
              </div>
            </div>
          </div>
        )}

        {/* Error Message */}
        {error && (
          <div className="bg-red-50 border-2 border-red-500 rounded-xl p-4 mb-6 flex items-center gap-3">
            <AlertCircle className="w-6 h-6 text-red-600" />
            <p className="text-red-800 font-medium">{error}</p>
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          {/* Booking Form - Enhanced */}
          <div 
            className="group relative bg-white rounded-2xl p-8 border-[3px] border-black shadow-xl overflow-hidden transition-all duration-500 hover:shadow-2xl"
            style={{
              animation: 'slideInScale 0.5s ease-out 0.1s both'
            }}
          >
            {/* Corner decorations */}
            <div className="absolute top-3 left-3 w-5 h-5 border-t-2 border-l-2 border-black/10 group-hover:border-[#3A86FF] transition-colors" />
            <div className="absolute bottom-3 right-3 w-5 h-5 border-b-2 border-r-2 border-black/10 group-hover:border-[#3A86FF] transition-colors" />
            
            {/* Shine effect */}
            <div className="absolute inset-0 bg-gradient-to-r from-transparent via-[#3A86FF]/5 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-1000" />
            
            <div className="relative z-10">
              <h2 className="text-2xl font-black text-black mb-6 flex items-center gap-2">
                <Calendar className="w-6 h-6 text-[#3A86FF]" />
                {t('consultation.bookAppointment')}
              </h2>

            {/* Date Selection - Enhanced */}
            <div className="mb-6">
              <label className="block text-sm font-bold text-black mb-3 uppercase tracking-wider flex items-center gap-2">
                <Calendar className="w-4 h-4 text-[#3A86FF]" />
                {t('consultation.selectDate')}
              </label>
              <div className="relative group/input">
                <Calendar className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-black/40 group-hover/input:text-[#3A86FF] transition-colors" />
                <input
                  type="date"
                  min={getMinDate()}
                  max={getMaxDate()}
                  value={selectedDate || ''}
                  onChange={(e) => handleDateSelect(e.target.value)}
                  className="w-full pl-12 pr-4 py-4 border-2 border-black/30 rounded-xl font-medium text-black focus:outline-none focus:border-[#3A86FF] transition-all duration-300 hover:border-black/50 hover:shadow-lg"
                />
              </div>
              {selectedDate && (
                <div 
                  className="mt-3 p-3 bg-black/5 rounded-lg border border-black/10"
                  style={{ animation: 'fadeIn 0.3s ease-out' }}
                >
                  <p className="text-sm font-medium text-black">{formatDate(selectedDate)}</p>
                </div>
              )}
            </div>

            {/* Time Slot Selection - Enhanced */}
            {selectedDate && (
              <div 
                className="mb-6"
                style={{ animation: 'fadeIn 0.4s ease-out' }}
              >
                <label className="block text-sm font-bold text-black mb-3 uppercase tracking-wider flex items-center gap-2">
                  <Clock className="w-4 h-4 text-[#3A86FF]" />
                  {t('consultation.selectTime')}
                </label>
                {loading ? (
                  <div className="text-center py-8">
                    <LogamiLogoSpinner size="lg" className="mx-auto" decorative={false} alt="Logami loading" />
                  </div>
                ) : availableSlots.length === 0 ? (
                  <div className="text-center py-8 bg-gray-50 rounded-xl border-2 border-dashed border-gray-300">
                    <Clock className="w-12 h-12 text-gray-400 mx-auto mb-2" />
                    <p className="text-black/60 font-medium">{t('consultation.noSlotsAvailable')}</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {/* Morning Slots (10:00-12:00) */}
                    <div>
                      <div className="text-xs font-bold text-black/60 mb-3 uppercase tracking-wider flex items-center gap-2">
                        <div className="w-2 h-2 bg-[#3A86FF] rounded-full"></div>
                        {t('consultation.morningSlots')}
                      </div>
                      <div className="grid grid-cols-3 gap-3">
                        {availableSlots.filter(slot => {
                          const hour = parseInt(slot.split(':')[0]);
                          return hour >= 10 && hour < 12;
                        }).map((slot, index) => (
                          <button
                            key={slot}
                            onClick={() => {
                              setSelectedTimeSlot(slot);
                              setError(null);
                            }}
                            style={{
                              animation: `fadeIn 0.3s ease-out ${index * 0.05}s both`
                            }}
                            className={`group relative py-3 px-4 rounded-lg border-2 font-medium transition-all duration-300 overflow-hidden ${
                              selectedTimeSlot === slot
                                ? 'bg-black text-white border-black shadow-lg scale-105'
                                : 'bg-white text-black border-black/30 hover:border-[#3A86FF] hover:shadow-md hover:scale-105'
                            }`}
                          >
                            {selectedTimeSlot === slot && (
                              <div className="absolute inset-0 bg-gradient-to-r from-[#3A86FF]/20 to-transparent" />
                            )}
                            <span className="relative z-10">{slot}</span>
                            {selectedTimeSlot === slot && (
                              <CheckCircle className="absolute top-1 right-1 w-4 h-4 text-[#3A86FF]" />
                            )}
                          </button>
                        ))}
                      </div>
                    </div>
                    
                    {/* Afternoon Slots (16:00-18:00) */}
                    <div>
                      <div className="text-xs font-bold text-black/60 mb-3 uppercase tracking-wider flex items-center gap-2">
                        <div className="w-2 h-2 bg-[#3A86FF] rounded-full"></div>
                        {t('consultation.afternoonSlots')}
                      </div>
                      <div className="grid grid-cols-3 gap-3">
                        {availableSlots.filter(slot => {
                          const hour = parseInt(slot.split(':')[0]);
                          return hour >= 16 && hour < 18;
                        }).map((slot, index) => (
                          <button
                            key={slot}
                            onClick={() => {
                              setSelectedTimeSlot(slot);
                              setError(null);
                            }}
                            style={{
                              animation: `fadeIn 0.3s ease-out ${index * 0.05}s both`
                            }}
                            className={`group relative py-3 px-4 rounded-lg border-2 font-medium transition-all duration-300 overflow-hidden ${
                              selectedTimeSlot === slot
                                ? 'bg-black text-white border-black shadow-lg scale-105'
                                : 'bg-white text-black border-black/30 hover:border-[#3A86FF] hover:shadow-md hover:scale-105'
                            }`}
                          >
                            {selectedTimeSlot === slot && (
                              <div className="absolute inset-0 bg-gradient-to-r from-[#3A86FF]/20 to-transparent" />
                            )}
                            <span className="relative z-10">{slot}</span>
                            {selectedTimeSlot === slot && (
                              <CheckCircle className="absolute top-1 right-1 w-4 h-4 text-[#3A86FF]" />
                            )}
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Notes - Enhanced */}
            <div className="mb-6">
              <label className="block text-sm font-bold text-black mb-3 uppercase tracking-wider">
                {t('consultation.notes')} <span className="text-black/40 normal-case">({t('common.optional')})</span>
              </label>
              <div className="relative group/textarea">
                <textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder={t('consultation.notesPlaceholder')}
                  rows={4}
                  className="w-full px-4 py-3 border-2 border-black/30 rounded-xl font-medium text-black focus:outline-none focus:border-[#3A86FF] transition-all duration-300 resize-none hover:border-black/50 hover:shadow-md"
                />
              </div>
            </div>

            {/* Book Button - Enhanced */}
            <button
              onClick={handleBookAppointment}
              disabled={!selectedDate || !selectedTimeSlot || booking}
              className={`group relative w-full py-4 rounded-xl font-black text-lg transition-all duration-300 overflow-hidden border-[3px] ${
                !selectedDate || !selectedTimeSlot || booking
                  ? 'bg-gray-300 text-gray-500 cursor-not-allowed border-gray-300'
                  : 'bg-black text-white border-black hover:bg-[#3A86FF] hover:border-[#3A86FF] hover:scale-105 hover:shadow-xl'
              }`}
            >
              {/* Shine effect */}
              {!booking && !(!selectedDate || !selectedTimeSlot || remainingConsultations <= 0) && (
                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-1000" />
              )}
              
              {/* Button content */}
              <span className="relative z-10 flex items-center justify-center gap-2">
                {booking ? (
                  <>
                    <LogamiLogoSpinner size="md" decorative={false} alt="Logami loading" />
                    {t('consultation.booking')}
                  </>
                ) : (
                  <>
                    <Calendar className="w-5 h-5" />
                    {t('consultation.bookNow')}
                  </>
                )}
              </span>
            </button>
            </div>
          </div>

          {/* Existing Appointments - Enhanced */}
          <div 
            className="group relative bg-white rounded-2xl p-8 border-[3px] border-black shadow-xl overflow-hidden transition-all duration-500 hover:shadow-2xl"
            style={{
              animation: 'slideInScale 0.5s ease-out 0.2s both'
            }}
          >
            {/* Corner decorations */}
            <div className="absolute top-3 left-3 w-5 h-5 border-t-2 border-l-2 border-black/10 group-hover:border-[#3A86FF] transition-colors" />
            <div className="absolute bottom-3 right-3 w-5 h-5 border-b-2 border-r-2 border-black/10 group-hover:border-[#3A86FF] transition-colors" />
            
            {/* Shine effect */}
            <div className="absolute inset-0 bg-gradient-to-r from-transparent via-[#3A86FF]/5 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-1000" />
            
            <div className="relative z-10">
              <h2 className="text-2xl font-black text-black mb-6 flex items-center gap-2">
                <Clock className="w-6 h-6 text-[#3A86FF]" />
                {t('consultation.myAppointments')}
              </h2>
              
              {userAppointments.length === 0 ? (
              <div className="text-center py-12">
                <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Clock className="w-10 h-10 text-black/20" />
                </div>
                <p className="text-black/60 font-medium">{t('consultation.noAppointments')}</p>
              </div>
            ) : (
              <div className="space-y-3">
                {userAppointments.map((appointment, index) => (
                  <div
                    key={appointment.id}
                    style={{
                      animation: `slideInScale 0.4s ease-out ${index * 0.1}s both`
                    }}
                    className="group/appointment relative border-2 border-black/30 rounded-xl p-4 hover:border-[#3A86FF] hover:shadow-lg transition-all duration-300 bg-white"
                  >
                    {/* Hover effect */}
                    <div className="absolute inset-0 bg-gradient-to-r from-[#3A86FF]/5 to-transparent opacity-0 group-hover/appointment:opacity-100 transition-opacity duration-300 rounded-xl" />
                    
                    <div className="relative z-10">
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex-1">
                          <p className="font-black text-black mb-2 text-lg">{formatDate(appointment.date)}</p>
                          <div className="flex items-center gap-2 text-black/70 mb-2">
                            <Clock className="w-4 h-4 text-[#3A86FF]" />
                            <span className="font-medium">{appointment.timeSlot}</span>
                            <span className="text-black/50">•</span>
                            <span className="text-sm">{appointment.duration} {t('consultation.minutes')}</span>
                          </div>
                          {appointment.notes && (
                            <div className="mt-2 p-2 bg-black/5 rounded-lg border border-black/10">
                              <p className="text-sm text-black/70">{appointment.notes}</p>
                            </div>
                          )}
                        </div>
                        <span className={`px-3 py-1.5 rounded-full text-xs font-bold flex-shrink-0 ml-3 ${
                          appointment.status === 'confirmed' ? 'bg-green-100 text-green-800 border-2 border-green-300' :
                          appointment.status === 'pending' ? 'bg-yellow-100 text-yellow-800 border-2 border-yellow-300' :
                          appointment.status === 'cancelled' ? 'bg-red-100 text-red-800 border-2 border-red-300' :
                          'bg-gray-100 text-gray-800 border-2 border-gray-300'
                        }`}>
                          {appointment.status}
                        </span>
                      </div>
                      
                      {/* Edit and Delete Buttons */}
                      {canEditOrDelete(appointment) && (
                        <div className="flex items-center gap-2 mt-3 pt-3 border-t border-black/10">
                          <button
                            onClick={() => handleEditAppointment(appointment)}
                            disabled={editingAppointment?.id === appointment.id || booking}
                            className="flex items-center gap-2 px-4 py-2 bg-[#3A86FF] text-white rounded-lg font-medium hover:bg-[#2D6ACC] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            <Edit className="w-4 h-4" />
                            {t('consultation.edit')}
                          </button>
                          <button
                            onClick={() => handleDeleteAppointment(appointment)}
                            disabled={deletingAppointment === appointment.id || booking}
                            className="flex items-center gap-2 px-4 py-2 bg-red-500 text-white rounded-lg font-medium hover:bg-red-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            <Trash2 className="w-4 h-4" />
                            {t('consultation.delete')}
                          </button>
                        </div>
                      )}
                      {!canEditOrDelete(appointment) && (
                        <div className="mt-3 pt-3 border-t border-black/10">
                          <p className="text-xs text-black/50 italic">{t('consultation.cannotEditDeleteLessThan24Hours')}</p>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
            </div>
          </div>
        </div>

        {/* Edit Appointment Modal */}
        {editingAppointment && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl p-8 border-[3px] border-black shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-black text-black flex items-center gap-2">
                  <Edit className="w-6 h-6 text-[#3A86FF]" />
                  {t('consultation.editAppointment')}
                </h2>
                <button
                  onClick={() => {
                    setEditingAppointment(null);
                    setEditDate('');
                    setEditTimeSlot('');
                    setEditNotes('');
                    setError(null);
                  }}
                  className="p-2 hover:bg-black/5 rounded-lg transition-colors"
                >
                  <X className="w-5 h-5 text-black/60" />
                </button>
              </div>

              {/* Date Selection */}
              <div className="mb-6">
                <label className="block text-sm font-bold text-black mb-3 uppercase tracking-wider flex items-center gap-2">
                  <Calendar className="w-4 h-4 text-[#3A86FF]" />
                  {t('consultation.selectDate')}
                </label>
                <input
                  type="date"
                  min={getMinDate()}
                  max={getMaxDate()}
                  value={editDate || ''}
                  onChange={(e) => handleEditDateChange(e.target.value)}
                  className="w-full px-4 py-4 border-2 border-black/30 rounded-xl font-medium text-black focus:outline-none focus:border-[#3A86FF] transition-all duration-300"
                />
              </div>

              {/* Time Slot Selection */}
              {editDate && (
                <div className="mb-6">
                  <label className="block text-sm font-bold text-black mb-3 uppercase tracking-wider flex items-center gap-2">
                    <Clock className="w-4 h-4 text-[#3A86FF]" />
                    {t('consultation.selectTime')}
                  </label>
                  {loading ? (
                    <div className="text-center py-8">
                      <LogamiLogoSpinner size="xl" className="mx-auto" decorative={false} alt="Logami loading" />
                    </div>
                  ) : editAvailableSlots.length === 0 ? (
                    <div className="text-center py-8 bg-gray-50 rounded-xl border-2 border-dashed border-gray-300">
                      <Clock className="w-12 h-12 text-gray-400 mx-auto mb-2" />
                      <p className="text-black/60 font-medium">{t('consultation.noSlotsAvailable')}</p>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {/* Morning Slots */}
                      <div>
                        <div className="text-xs font-bold text-black/60 mb-3 uppercase tracking-wider flex items-center gap-2">
                          <div className="w-2 h-2 bg-[#3A86FF] rounded-full"></div>
                          {t('consultation.morningSlots')}
                        </div>
                        <div className="grid grid-cols-3 gap-3">
                          {editAvailableSlots.filter(slot => {
                            const hour = parseInt(slot.split(':')[0]);
                            return hour >= 10 && hour < 12;
                          }).map((slot) => (
                            <button
                              key={slot}
                              onClick={() => {
                                setEditTimeSlot(slot);
                                setError(null);
                              }}
                              className={`py-3 px-4 rounded-lg border-2 font-medium transition-all duration-300 ${
                                editTimeSlot === slot
                                  ? 'bg-black text-white border-black shadow-lg'
                                  : 'bg-white text-black border-black/30 hover:border-[#3A86FF] hover:shadow-md'
                              }`}
                            >
                              {slot}
                            </button>
                          ))}
                        </div>
                      </div>
                      
                      {/* Afternoon Slots */}
                      <div>
                        <div className="text-xs font-bold text-black/60 mb-3 uppercase tracking-wider flex items-center gap-2">
                          <div className="w-2 h-2 bg-[#3A86FF] rounded-full"></div>
                          {t('consultation.afternoonSlots')}
                        </div>
                        <div className="grid grid-cols-3 gap-3">
                          {editAvailableSlots.filter(slot => {
                            const hour = parseInt(slot.split(':')[0]);
                            return hour >= 16 && hour < 18;
                          }).map((slot) => (
                            <button
                              key={slot}
                              onClick={() => {
                                setEditTimeSlot(slot);
                                setError(null);
                              }}
                              className={`py-3 px-4 rounded-lg border-2 font-medium transition-all duration-300 ${
                                editTimeSlot === slot
                                  ? 'bg-black text-white border-black shadow-lg'
                                  : 'bg-white text-black border-black/30 hover:border-[#3A86FF] hover:shadow-md'
                              }`}
                            >
                              {slot}
                            </button>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Notes */}
              <div className="mb-6">
                <label className="block text-sm font-bold text-black mb-3 uppercase tracking-wider">
                  {t('consultation.notes')} <span className="text-black/40 normal-case">({t('common.optional')})</span>
                </label>
                <textarea
                  value={editNotes}
                  onChange={(e) => setEditNotes(e.target.value)}
                  placeholder={t('consultation.notesPlaceholder')}
                  rows={4}
                  className="w-full px-4 py-3 border-2 border-black/30 rounded-xl font-medium text-black focus:outline-none focus:border-[#3A86FF] transition-all duration-300 resize-none"
                />
              </div>

              {/* Action Buttons */}
              <div className="flex items-center gap-3">
                <button
                  onClick={handleSaveEdit}
                  disabled={!editDate || !editTimeSlot || booking}
                  className={`flex-1 py-4 rounded-xl font-black text-lg transition-all duration-300 ${
                    !editDate || !editTimeSlot || booking
                      ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                      : 'bg-black text-white hover:bg-[#3A86FF] hover:scale-105'
                  }`}
                >
                  {booking ? t('consultation.saving') : t('consultation.saveChanges')}
                </button>
                <button
                  onClick={() => {
                    setEditingAppointment(null);
                    setEditDate('');
                    setEditTimeSlot('');
                    setEditNotes('');
                    setError(null);
                  }}
                  className="px-6 py-4 bg-gray-200 text-black rounded-xl font-bold hover:bg-gray-300 transition-colors"
                >
                  {t('common.cancel')}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ConsultationBookingPage;
